import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House, Eye, EyeSlash } from "@phosphor-icons/react";
import pkg from "../../package.json";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/";
  const prefillEmail = params.get("email") || "";
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate(nextPath);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-100">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1631630259742-c0f0b17c6c10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb3p5JTIwaG9tZSUyMGludGVyaW9yJTIwd2FybXxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
          alt="Cozy home interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-stoneink-900/50 via-stoneink-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">A place for<br/>every little thing.</div>
          <p className="mt-4 text-white/85 max-w-md">Remember where you tucked away the holiday lights, the spare batteries, your grandmother's recipe box.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="login-logo-home-link" className="flex items-center gap-3 mb-10 group w-fit">
            <div className="h-12 w-12 rounded-2xl bg-terracotta-500 text-white grid place-items-center shadow-ambient group-hover:shadow-hover transition-shadow">
              <House weight="duotone" size={26} />
            </div>
            <div>
              <div className="font-heading text-2xl font-semibold text-stoneink-900 group-hover:text-terracotta-600 transition-colors">Nestled</div>
              <div className="text-xs text-stoneink-500">welcome home</div>
            </div>
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Welcome back</h1>
          <p className="mt-3 text-stoneink-700">Sign in to find where everything lives.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none transition-all"
                placeholder="you@home.app"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Password</label>
              <div className="relative mt-2">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-4 pr-12 rounded-xl bg-white border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-lg text-stoneink-500 hover:text-stoneink-900 hover:bg-sand-200 transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end -mt-2">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-sm text-terracotta-600 hover:text-terracotta-700 font-medium underline-offset-4 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <button
              data-testid="login-submit-btn"
              type="submit" disabled={busy}
              className="w-full h-12 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium transition-all active:scale-[0.99] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-sm text-stoneink-700">
            New here?{" "}
            <Link to="/register" data-testid="go-to-register" className="text-terracotta-600 font-medium hover:text-terracotta-700 underline-offset-4 hover:underline">
              Create an account
            </Link>
          </p>

          <div className="mt-12 text-center text-xs text-stoneink-500" data-testid="login-version">
            Nestled · v{pkg.version}
          </div>
        </div>
      </div>
    </div>
  );
}
